import {
  ChangeEvent,
  Dispatch,
  FunctionComponent,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import {
  StoryCardArticleParsedUrl,
  StoryCardParseUrlPayload,
  StoryCardQuoteParsedUrl,
  StoryCardQuoteSource,
  StoryCardTeaserImageStyle,
  StoryCardType,
  User,
  storyCardApi,
} from '../../../../services';
import { GalleryItem, ImageFile } from '../../../models';
import {
  getSocialQuoteMetaData,
  isFacebookUrl,
  isInstagramUrl,
  isMediaParsed,
  isUrl,
} from '../../../utils';
import { InputField } from '../../InputField';
import { LinkPostArticleFields, LinkPostFields, LinkPostQuoteFields } from '../CreateCardModal';
import { LinkPostArticle } from './LinkPostArticle';
import { LinkPostQuote } from './LinkPostQuote';

import classNames from 'classnames';
import classes from './LinkPost.module.scss';

interface LinkPostProps {
  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  linkPostFields: LinkPostFields;
  setLinkPostFields: Dispatch<SetStateAction<LinkPostFields>>;
  linkPostPrevUrl: string;
  setLinkPostPrevUrl: Dispatch<SetStateAction<string>>;
  setSettingsOpen: (isOpen: boolean) => void;
  canOpenSettings: boolean;
  setActiveLinkPostSettingsTabIndex: (index: number) => void;
  setLinkPostArticleGalleryItems: (galleryItems: GalleryItem[]) => void;
  setLinkPostQuoteGalleryItems: (galleryItems: GalleryItem[]) => void;
  postInStoryTeaserStyle: StoryCardTeaserImageStyle;
  mentionedUsers: User[];
  setUserMentionModalOpen: Dispatch<SetStateAction<boolean>>;
  postInStoryAllowMentions: boolean;
}

export const LinkPost: FunctionComponent<LinkPostProps> = memo(
  ({
    loading,
    setLoading,
    linkPostFields,
    setLinkPostFields,
    linkPostPrevUrl,
    setLinkPostPrevUrl,
    setSettingsOpen,
    canOpenSettings,
    setActiveLinkPostSettingsTabIndex,
    setLinkPostArticleGalleryItems,
    setLinkPostQuoteGalleryItems,
    postInStoryTeaserStyle,
    mentionedUsers,
    setUserMentionModalOpen,
    postInStoryAllowMentions,
  }) => {
    const { t } = useTranslation();

    const [invalidUrl, setInvalidUrl] = useState(false);

    const [parseUrl] = storyCardApi.endpoints.storyCardParseUrl.useMutation();

    const { url, type, article, quote } = linkPostFields;

    const renderInput = useMemo(
      () =>
        Boolean(
          !loading && (!type || ![StoryCardType.ARTICLE, StoryCardType.QUOTE].includes(type))
        ),
      [loading, type]
    );

    const processParsedData = useCallback(
      async (url: string, parsedData: StoryCardParseUrlPayload) => {
        const { type } = parsedData;

        if (![StoryCardType.ARTICLE, StoryCardType.QUOTE].includes(type)) {
          setInvalidUrl(true);
          setLoading(false);
          return;
        }

        const parsedLinkPostFields: LinkPostFields = { url, type };

        switch (type) {
          case StoryCardType.ARTICLE:
            parsedLinkPostFields.article = {
              ...parsedData,
              headline: article?.headline ?? '',
              styles: article?.styles ?? { teaserImageStyle: postInStoryTeaserStyle },
            } as LinkPostArticleFields;
            break;
          case StoryCardType.QUOTE:
            parsedLinkPostFields.quote = {
              ...parsedData,
              headline: quote?.headline ?? '',
            } as LinkPostQuoteFields;
            break;
        }

        const { gallery } = parsedData as StoryCardArticleParsedUrl | StoryCardQuoteParsedUrl;
        const { externalId } = { ...gallery[0]?.image };

        try {
          await isMediaParsed(externalId);
        } catch (_) {
          toast.error(t('common.media-parse-error'));

          switch (type) {
            case StoryCardType.ARTICLE:
              parsedLinkPostFields.article = {
                ...parsedLinkPostFields.article,
                gallery: [],
              } as LinkPostArticleFields;
              break;
            case StoryCardType.QUOTE:
              parsedLinkPostFields.quote = {
                ...parsedLinkPostFields.quote,
                gallery: [],
              } as LinkPostQuoteFields;
              break;
          }
        }

        setLinkPostFields(parsedLinkPostFields);
        setLoading(false);
      },
      [
        article?.headline,
        article?.styles,
        postInStoryTeaserStyle,
        quote?.headline,
        setLinkPostFields,
        setLoading,
        t,
      ]
    );

    const linkCheck = useCallback(
      async (value: string) => {
        if (value && !isUrl(value)) {
          setInvalidUrl(true);
          return;
        }

        if (value) {
          setLoading(true);

          const parsedData = await parseUrl({ url: value }).unwrap();

          processParsedData(
            value,
            parsedData ?? {
              type: StoryCardType.ARTICLE,
              sourceName: '',
              title: '',
              abstract: '',
              contentAuthor: '',
              gallery: [],
            }
          );
        }
      },
      [parseUrl, processParsedData, setLoading]
    );

    const socialQuoteCheck = useCallback(
      async (url: string) => {
        setLoading(true);

        const {
          quote,
          quoteSource,
          quoteCreated,
          quotePerson,
          quotePersonHandle,
          quotePersonImageUrl,
          gallery: galleryImageUrls,
        } = await getSocialQuoteMetaData(url);

        if (!quote) {
          linkCheck(url);
          return;
        }

        const parseImageUrl = async (url?: string) => {
          if (!url) {
            return;
          }

          return (await parseUrl({ url }).unwrap()).gallery[0] as GalleryItem | undefined;
        };

        const gallery: GalleryItem[] = [];

        let quotePersonImage: ImageFile | undefined;

        try {
          quotePersonImage = (await parseImageUrl(quotePersonImageUrl))?.image;

          for (const { imageUrl } of galleryImageUrls) {
            gallery.push({ ...(await parseImageUrl(imageUrl)), title: '' } as GalleryItem);
          }
        } catch (_) {
          toast.error(t('fileUpload.error'));
        }

        setLinkPostFields({
          url,
          type: StoryCardType.QUOTE,
          quote: {
            type: StoryCardType.QUOTE,
            quoteSource: quoteSource as StoryCardQuoteSource,
            headline: '',
            quote,
            quoteCreated,
            quotePerson,
            quotePersonHandle,
            quotePersonImage,
            gallery,
          },
        });

        setLoading(false);
      },
      [linkCheck, parseUrl, setLinkPostFields, setLoading, t]
    );

    useEffect(() => {
      if (!url) {
        setInvalidUrl(false);
      }

      if (url && url !== linkPostPrevUrl) {
        if (isInstagramUrl(url) || isFacebookUrl(url)) {
          socialQuoteCheck(url);
        } else {
          linkCheck(url);
        }

        setLinkPostPrevUrl(url);
      }
    }, [linkCheck, linkPostPrevUrl, setLinkPostPrevUrl, socialQuoteCheck, url]);

    const inputChangeHandler = useCallback(
      ({ target }: ChangeEvent<HTMLInputElement>) => {
        setLinkPostFields({ ...linkPostFields, url: target.value });
      },
      [linkPostFields, setLinkPostFields]
    );

    const setArticle = useCallback(
      (article: LinkPostArticleFields) => {
        setLinkPostFields({ ...linkPostFields, article });
      },
      [linkPostFields, setLinkPostFields]
    );

    const setQuote = useCallback(
      (quote: LinkPostQuoteFields) => {
        setLinkPostFields({ ...linkPostFields, quote });
      },
      [linkPostFields, setLinkPostFields]
    );

    const renderParsedData = useMemo(() => {
      switch (type) {
        case StoryCardType.ARTICLE:
          return (
            <LinkPostArticle
              loading={loading}
              setLoading={setLoading}
              url={url}
              articleData={article as LinkPostArticleFields}
              setArticle={setArticle}
              setLinkPostArticleGalleryItems={setLinkPostArticleGalleryItems}
              setActiveLinkPostSettingsTabIndex={setActiveLinkPostSettingsTabIndex}
              setSettingsOpen={setSettingsOpen}
              canOpenSettings={canOpenSettings}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
            />
          );
        case StoryCardType.QUOTE:
          return (
            <LinkPostQuote
              loading={loading}
              setLoading={setLoading}
              url={url}
              quoteData={quote as LinkPostQuoteFields}
              setQuote={setQuote}
              setLinkPostQuoteGalleryItems={setLinkPostQuoteGalleryItems}
              setActiveLinkPostSettingsTabIndex={setActiveLinkPostSettingsTabIndex}
              setSettingsOpen={setSettingsOpen}
              canOpenSettings={canOpenSettings}
              mentionedUsers={mentionedUsers}
              setUserMentionModalOpen={setUserMentionModalOpen}
              postInStoryAllowMentions={postInStoryAllowMentions}
            />
          );
        default:
          return null;
      }
    }, [
      article,
      canOpenSettings,
      loading,
      mentionedUsers,
      postInStoryAllowMentions,
      quote,
      setActiveLinkPostSettingsTabIndex,
      setArticle,
      setLinkPostArticleGalleryItems,
      setLinkPostQuoteGalleryItems,
      setLoading,
      setQuote,
      setSettingsOpen,
      setUserMentionModalOpen,
      type,
      url,
    ]);

    const renderLoader = useMemo(() => Boolean(loading && !type), [loading, type]);

    return (
      <div
        className={classNames(classes['link-post'], {
          [classes['link-post--mobile']]: isMobileOnly,
        })}
      >
        {renderInput && (
          <div className={classes['link-post__input']}>
            <InputField
              value={url}
              placeholder={t('addContent.link-post-url-placeholder')}
              onChange={inputChangeHandler}
              error={invalidUrl}
              errorMessage={t('common.url-invalid')}
              disabled={loading}
              borderless
            />
          </div>
        )}
        {renderLoader && (
          <div className={classes['link-post__loader']}>
            <Skeleton height={'3rem'} />
          </div>
        )}
        {renderParsedData}
      </div>
    );
  }
);
